"use client";

import SubHeader from "@/components/SubHeader";
import { isFilled } from "@prismicio/client";
import { PrismicRichText } from "@prismicio/react";
import { Splide, SplideSlide } from "@splidejs/react-splide";
import { getHighlightClassName, getTitleComponents } from "@/helpers";
import { useId } from "react";
import "./method-slider.css";
import Tag from "@/components/Tag";

const TAG_COLORS = ["rouge-50", "bleu-50", "vert-50"];

/**
 * @param {import("@prismicio/client").Content.ReviewSliceDefaultPrimaryMethodsItem} item
 * @param {number} index
 */
const MethodCard = ({ item, index, className = "", titleLevel = 3, titleId }) => {
  const stepNumber = `${String(index + 1).padStart(2, "0")}.`;
  const tags = [item.tag_1, item.tag_2, item.tag_3].filter(isFilled.keyText);
  const tagColor = TAG_COLORS[index % TAG_COLORS.length];
  const TitleTag = `h${titleLevel}`;

  const hasTitle = isFilled.richText(item.title);
  const hasText = isFilled.richText(item.text);

  if (!hasTitle && !hasText && tags.length === 0) {
    return null;
  }

  return (
    <div
      className={`flex h-auto flex-col gap-6 rounded-[30px] bg-beige-30 p-[30px] md:rounded-[40px] md:p-10 md:px-[30px] ${className}`}
    >
      <div className="flex flex-col gap-5 lg:gap-12">
        <div className="flex flex-col gap-5">
          <p className="text-number text-bleu" aria-hidden>
            {stepNumber}
          </p>
          <PrismicRichText
            field={item.title}
            components={{
              heading2: ({ children }) => (
                <TitleTag id={titleId} className="h2-xl lg:h2-desktop-xl text-bleu">
                  <span className="sr-only">{`Étape ${index + 1}. `}</span>
                  {children}
                </TitleTag>
              ),
              heading3: ({ children }) => (
                <TitleTag id={titleId} className="h2-xl lg:h2-desktop-xl text-bleu">
                  <span className="sr-only">{`Étape ${index + 1}. `}</span>
                  {children}
                </TitleTag>
              ),
              heading4: ({ children }) => (
                <TitleTag id={titleId} className="h2-xl lg:h2-desktop-xl text-bleu">
                  <span className="sr-only">{`Étape ${index + 1}. `}</span>
                  {children}
                </TitleTag>
              ),
            }} />
        </div>

        <div className="lg:hidden">
          {tags.length > 0 && (
            <div className="flex flex-wrap gap-2.5">
              <Tag key={`method-tag-${index}-${0}`} text={tags[0]} color={tagColor} />
            </div>
          )}
        </div>

        <div className="hidden lg:block">
          {tags.length > 0 && (
            <div className="flex flex-wrap gap-2.5">
              {tags.map((tag, tagIndex) => (
                <Tag key={`method-tag-${index}-${tagIndex}`} text={tag} color={tagColor} />
              ))}
            </div>
          )}
        </div>

      </div>
      <PrismicRichText field={item.text} components={{
        paragraph: ({ children }) => (
          <p className="p-base md:p-lg text-bleu">{children}</p>
        ),
      }} />
    </div>
  );
};

const REVIEW_TAG_COLORS = {
  "Site web": "vert-50",
  "Outil métier": "rouge",
  Application: "rouge-50",
};

/**
 * @param {import("@prismicio/client").Content.ReviewSliceReviewPrimaryMethodsItem} item
 */
const ReviewCard = ({ item, className = "", isSimpleVariation = false }) => {
  const tagColor = REVIEW_TAG_COLORS[item.tag] ?? "vert-50";
  const hasText = isFilled.richText(item.text);
  const hasTag = isFilled.keyText(item.tag);
  const hasAuthor = isFilled.keyText(item.author);

  if (!hasText && !hasTag && !hasAuthor) {
    return null;
  }

  return (
    <div
      className={`flex h-auto flex-col gap-5 justify-between bg-beige-30 p-[30px] rounded-[20px] md:rounded-[30px] md:pb-10 ${className}`}
    >
      <div className="flex flex-col gap-5">
        {hasTag && (
          <Tag
            text={item.tag}
            color={tagColor}
            className={`${isSimpleVariation ? "" : "italic "}text-beige-30`}
          />
        )}

        {hasText && (
          <PrismicRichText field={item.text} components={{
            paragraph: ({ children }) => (
              <p className={`p-base md:p-lg text-bleu ${isSimpleVariation ? "" : "italic"}`}>{children}</p>
            )
          }} />
        )}
      </div>

      {hasAuthor && (
        <p className={`name-text uppercase text-bleu`}>
          <span className={`${getHighlightClassName('beige')}`}>{item.author}</span>
        </p>
      )}
    </div>
  );
};

/**
 * Testimonial card of the `review` variation (italic quote, author, position).
 * @param {import("@prismicio/client").Content.ReviewSliceReviewPrimaryReviewsItem} item
 */
const TestimonialCard = ({ item, className = "" }) => {
  const hasText = isFilled.richText(item.text);
  const hasAuthor = isFilled.keyText(item.author);
  const hasPosition = isFilled.keyText(item.position);

  if (!hasText && !hasAuthor && !hasPosition) {
    return null;
  }

  return (
    <figure
      className={`flex h-auto flex-col justify-between gap-10 rounded-[30px] bg-blanc p-[30px] text-bleu ${className}`}
    >
      {hasText && (
        <blockquote>
          <PrismicRichText
            field={item.text}
            components={{
              paragraph: ({ children }) => (
                <p className="p-lg-n italic text-bleu not-last:mb-[7px]">{children}</p>
              ),
              strong: ({ children }) => <strong className="md">{children}</strong>,
              hyperlink: ({ children, node }) => (
                <a href={node.data.url} className="underline underline-offset-2">
                  {children}
                </a>
              ),
            }}
          />
        </blockquote>
      )}

      {(hasAuthor || hasPosition) && (
        <figcaption className="flex flex-col gap-1">
          {hasAuthor && <p className="p-lg-n sbd text-bleu">{item.author}</p>}
          {hasPosition && <p className="p-base-n text-bleu">{item.position}</p>}
        </figcaption>
      )}
    </figure>
  );
};

/**
 * @typedef {import("@prismicio/client").Content.ReviewSlice} ReviewSlice
 * @typedef {import("@prismicio/react").SliceComponentProps<ReviewSlice>} CarouselProps
 * @type {import("react").FC<CarouselProps>}
 */
const Carousel = ({ slice }) => {
  const carouselHeadingId = useId();
  const { sub_header, title, sub_title, methods, reviews } = slice.primary;
  const isReviewVariation = slice.variation === "review";
  const isSimpleVariation = slice.variation === "simple";
  const isReviewLikeVariation = isReviewVariation || isSimpleVariation;
  const hasSectionTitle = isFilled.richText(title);
  const hasSubTitle = isFilled.richText(sub_title);
  const methodCardTitleLevel = hasSubTitle ? 4 : 3;

  const sectionTitleComponents = isReviewVariation ? {
    heading2: ({ children }) => (
      <h2 id={carouselHeadingId} className="h2-n lg:dk text-bleu">
        {children}
      </h2>
    ),
    heading3: ({ children }) => (
      <h3 id={carouselHeadingId} className="h2-n lg:dk text-bleu">
        {children}
      </h3>
    ),
  } : {
    ...getTitleComponents({
      color: "bleu",
      highlightColor: "beige-30",
    }),
    heading2: ({ children }) => (
      <h2 id={carouselHeadingId} className="h2 md:h2-desktop text-bleu">
        {children}
      </h2>
    ),
    heading3: ({ children }) => (
      <h3 id={carouselHeadingId} className="h2 md:h2-desktop text-bleu">
        {children}
      </h3>
    ),
  };

  const carouselAriaProps = hasSectionTitle
    ? { "aria-labelledby": carouselHeadingId }
    : {
        "aria-label": isReviewLikeVariation
          ? "Témoignages clients"
          : "Étapes de notre méthode",
      };

  const items = isReviewVariation ? reviews : methods;
  const filledMethods = Array.isArray(items)
    ? items.filter((item) => {
      if (isReviewVariation) {
        return (
          isFilled.richText(item.text) ||
          isFilled.keyText(item.author) ||
          isFilled.keyText(item.position)
        );
      }

      if (isSimpleVariation) {
        return (
          isFilled.richText(item.text) ||
          isFilled.keyText(item.tag) ||
          isFilled.keyText(item.author)
        );
      }

      return (
        isFilled.richText(item.title) ||
        isFilled.richText(item.text) ||
        [item.tag_1, item.tag_2, item.tag_3].some(isFilled.keyText)
      );
    })
    : [];

  // 3 cards or fewer: no carousel on large screens, the cards share the full width.
  const isGridLayout = filledMethods.length <= 3;

  const renderDesktopCard = (item, index, titleId) =>
    isReviewVariation ? (
      <TestimonialCard item={item} className="h-full w-full" />
    ) : isSimpleVariation ? (
      <ReviewCard item={item} className="h-full w-full" isSimpleVariation={isSimpleVariation} />
    ) : (
      <MethodCard
        item={item}
        index={index}
        titleLevel={methodCardTitleLevel}
        titleId={titleId}
        className="h-full w-full"
      />
    );

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className={`contre-uno ${isReviewVariation ? "bg-beige-30 py-15 lg:py-20" : "bg-beige padding-y"}`}
    >
      <div className={`flex flex-col gap-10 md:items-end ${isReviewVariation ? "md:gap-[75px]" : "md:gap-20"}`}>
        <div className="padding-x w-full">
          <div className="flex w-full flex-col gap-3 md:gap-4 framing-lg">
            {isFilled.keyText(sub_header) && <SubHeader text={sub_header} />}
            {isFilled.richText(title) && (
              <PrismicRichText
                field={title}
                components={sectionTitleComponents}
              />
            )}
            {isFilled.richText(sub_title) && (
              <div className="md:max-w-3xl">
                <PrismicRichText field={sub_title} components={{
                  heading3: ({ children }) => (
                    <h3 className="p-lg-medium md:p-xl-semibold text-bleu">{children}</h3>
                  ),
                }} />
              </div>
            )}
          </div>
        </div>

        {filledMethods.length > 0 && (
          <div className="flex flex-col gap-5 w-full ">
            <div className="flex w-full snap-x snap-mandatory gap-5 overflow-x-auto scroll-smooth px-5 pb-2 [-ms-overflow-style:none] [scrollbar-width:none] md:hidden [&::-webkit-scrollbar]:hidden">
              {filledMethods.map((item, index) =>
                isReviewVariation ? (
                  <TestimonialCard
                    key={`review-mobile-${index}`}
                    item={item}
                    className="w-[310px] max-w-[84vw] shrink-0 snap-center self-stretch"
                  />
                ) : isSimpleVariation ? (
                  <ReviewCard
                    key={`review-mobile-${index}`}
                    item={item}
                    className="w-[84vw] shrink-0 snap-center"
                    isSimpleVariation={isSimpleVariation}
                  />
                ) : (
                  <MethodCard
                    key={`method-mobile-${index}`}
                    item={item}
                    index={index}
                    titleLevel={methodCardTitleLevel}
                    titleId={`${carouselHeadingId}-card-${index}`}
                    className="w-[84vw] shrink-0 snap-center"
                  />
                ),
              )}
            </div>

            <div className={`method-splide framing-xl hidden w-full md:block ${isReviewVariation ? "method-splide-review" : ""} ${isGridLayout ? "lg:hidden" : ""}`}>
              <Splide
                options={isReviewVariation ? {
                  type: "slide",
                  fixedWidth: "383px",
                  gap: "20px",
                  pagination: false,
                  arrows: true,
                  trimSpace: false,
                  padding: { left: "92px", right: "0px" },
                  breakpoints: {
                    1180: {
                      padding: { left: "2.5rem", right: "0px" },
                    },
                  },
                } : {
                  type: "slide",
                  perPage: slice.variation === 'default' ? 3 : 3,
                  perMove: 1,
                  gap: slice.variation === 'default' ? "40px" : "20px",
                  pagination: false,
                  arrows: true,
                  trimSpace: false,
                  padding: slice.variation === "default"
                    ? { left: "40px", right: "40px" }
                    : { left: "20px", right: "20px" },
                  breakpoints: {
                    1600: {
                      gap: "20px",
                      padding: slice.variation === "default"
                        ? { left: "92px", right: "92px" }
                        : { left: "92px", right: "92px" },
                    },
                    1400: {
                      perPage: slice.variation === "default" ? 3 : 2,
                      gap: "20px",
                      padding: slice.variation === "default"
                        ? { left: "92px", right: "92px" }
                        : { left: "92px", right: "92px" },
                    },
                    1180: {
                      perPage: slice.variation === "default" ? 2 : 2,
                      gap: "20px",
                      padding: slice.variation === "default"
                        ? { left: "2.5rem", right: "2.5rem" }
                        : { left: "2.5rem", right: "2.5rem" },
                    },
                  },
                }}
                {...carouselAriaProps}
              >
                {filledMethods.map((item, index) => {
                  const slideLabelId = `${carouselHeadingId}-slide-${index}`;

                  return (
                  <SplideSlide
                    key={`carousel-desktop-${index}`}
                    className="h-auto"
                    aria-labelledby={
                      isReviewLikeVariation ? undefined : slideLabelId
                    }
                  >
                    {isReviewVariation ? (
                      <TestimonialCard item={item} className="h-full" />
                    ) : isSimpleVariation ? (
                      <ReviewCard
                        item={item}
                        className="h-full"
                        isSimpleVariation={isSimpleVariation}
                      />
                    ) : (
                      <MethodCard
                        item={item}
                        index={index}
                        titleLevel={methodCardTitleLevel}
                        titleId={slideLabelId}
                        className="h-full"
                      />
                    )}
                  </SplideSlide>
                  );
                })}

              </Splide>
            </div>

            {isGridLayout && (
              <div className="padding-x hidden w-full lg:block">
                <ul
                  className={`framing-lg grid gap-5 ${slice.variation === "default" ? "min-[1600px]:gap-10" : ""}`}
                  style={{ gridTemplateColumns: `repeat(${filledMethods.length}, minmax(0, 1fr))` }}
                >
                  {filledMethods.map((item, index) => (
                    <li key={`carousel-grid-${index}`} className="flex">
                      {renderDesktopCard(item, index, `${carouselHeadingId}-grid-${index}`)}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default Carousel;
